import { Lock, X } from 'lucide-react'
import { Button, EMPLOYMENT_TYPE_LABELS, LoadFailure, Modal, Skeleton, TIER_LABELS } from '../../../core/index.js'
import { useWorkerRoster } from '../../../roster/index.js'
import { useScheduleBoard, useShiftPreferences } from '../../hooks.js'
import type { PostingShift, ShiftTypeKey } from '../../types.js'
import { buildWorkerMonth, type WorkerMonthDay } from './workerMonthData.js'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
const SHIFT_LABEL: Record<ShiftTypeKey, string> = { day: 'Day', evening: 'Evening', night: 'Night' }
const SHIFT_CLASS: Record<ShiftTypeKey, string> = { day: 'bg-marine-lt text-marine-dk', evening: 'border border-marine/15 bg-silver-lt text-ink', night: 'bg-marine-dk text-white' }

function monthName(period: string): string {
  return new Date(`${period}-01T00:00:00Z`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

function DayCell({ day, seat }: { day: WorkerMonthDay; seat: PostingShift }) {
  const ground = !day.inMonth ? 'bg-canvas' : day.inRun ? 'bg-gold-lt' : 'bg-white'
  return (
    <div className={`flex min-h-[3.6rem] flex-col gap-0.5 border-b border-r border-marine/15 p-1 ${ground} ${day.seat ? 'outline-dashed outline-2 -outline-offset-[3px] outline-coral' : ''}`}>
      <span className={`text-[10px] font-bold tabular-nums text-ink-mid ${day.inMonth ? '' : 'opacity-40'}`}>{day.dayOfMonth}</span>
      {day.inMonth && day.assignment && (
        <span className={`rounded px-1 py-0.5 text-[10px] font-bold leading-tight ${SHIFT_CLASS[day.assignment.shiftType]}`}>
          {SHIFT_LABEL[day.assignment.shiftType]}
          <span className="block font-mono text-[9px] font-semibold opacity-80">
            {day.assignment.startTime}–{day.assignment.endTime}
            {day.assignment.unitId !== seat.unitId ? ` · ${day.assignment.unitId}` : ''}
          </span>
        </span>
      )}
      {day.inMonth && day.seat && (
        <span className="rounded border border-dashed border-coral bg-coral-lt px-1 py-0.5 text-[10px] font-bold leading-tight text-coral">
          {SHIFT_LABEL[seat.shiftType]} · this seat
          <span className="block font-mono text-[9px] font-semibold opacity-80">
            {seat.startTime}–{seat.endTime}
          </span>
        </span>
      )}
      {day.inMonth && !day.assignment && day.pto && <span className="rounded bg-green-lt px-1 py-0.5 text-[10px] font-bold text-green">PTO</span>}
      {day.inMonth && !day.assignment && !day.pto && day.dayOff && <span className="rounded border border-dashed border-marine/30 px-1 py-0.5 text-[10px] font-semibold text-ink-mid">Asked off · {day.dayOff}</span>}
    </div>
  )
}

function Legend() {
  const items = [
    ['bg-marine-lt', 'Day'],
    ['border border-marine/15 bg-silver-lt', 'Evening'],
    ['bg-marine-dk', 'Night'],
    ['bg-green-lt', 'PTO'],
    ['border border-dashed border-ink-mid', 'Asked off'],
    ['bg-gold-lt', 'Days in a row'],
    ['outline-dashed outline-2 -outline-offset-2 outline-coral', 'This seat'],
  ] as const
  return (
    <div className="flex flex-wrap gap-x-3.5 gap-y-1 text-[10px] text-ink-mid">
      {items.map(([swatch, label]) => (
        <span key={label} className="inline-flex items-center gap-1">
          <i className={`inline-block h-2.5 w-3 rounded-sm ${swatch}`} aria-hidden="true" />
          {label}
        </span>
      ))}
    </div>
  )
}

interface WorkerMonthModalProps {
  facilityId: string
  period: string
  workerId: string
  /** The open seat the month is looked at for; its month is the one shown. */
  seat: PostingShift
  onClose: () => void
}

/**
 * One worker's month, read only, opened from an open seat's Assign pill (the user's design, 26 October 2026): their
 * shifts as the board holds them, PTO and days asked off from their preferences, the seat outlined on its day and the
 * days in a row it would join shaded, with hours per Sunday-to-Saturday week. Only the seat's month; nothing changes here.
 */
export function WorkerMonthModal({ facilityId, period, workerId, seat, onClose }: WorkerMonthModalProps) {
  const board = useScheduleBoard(facilityId, period)
  const preferences = useShiftPreferences(workerId, period)
  const roster = useWorkerRoster(facilityId)
  const worker = roster.data?.find((row) => row.workerId === workerId) ?? null
  const month = board.data?.schedule.find((entry) => entry.workerId === workerId) ?? null
  const name = month?.name ?? worker?.name ?? 'Worker'
  const prefs = preferences.data?.preferences ?? null

  const subtitle = worker ? (
    <span className="flex flex-wrap gap-x-2.5">
      <span>{worker.role}</span>
      <span>
        {TIER_LABELS[worker.tier]} · {worker.score}
      </span>
      <span>{EMPLOYMENT_TYPE_LABELS[worker.employmentType ?? 'full_time']}</span>
      {month && <span>{month.hours} h scheduled in {monthName(period).split(' ')[0]}</span>}
    </span>
  ) : undefined

  let body
  if (board.isPending) {
    body = <Skeleton lines={6} className="p-6" />
  } else if (board.isError) {
    body = <LoadFailure size="xs" message="The month could not be loaded." onRetry={() => void board.refetch()} retrying={board.isFetching} />
  } else {
    const weeks = buildWorkerMonth({ period, assignments: month?.assignments ?? [], ptoDates: prefs?.ptoDates ?? [], daysOff: prefs?.daysOff ?? [], seatDate: seat.date })
    body = (
      <div className="flex flex-col gap-3 p-6">
        <div className="text-sm font-black text-ink">{monthName(period)}</div>
        <div className="overflow-x-auto">
          <div className="grid min-w-[30rem] grid-cols-[repeat(7,minmax(3.5rem,1fr))_3.6rem] border-l border-t border-marine/15">
            {WEEKDAYS.map((weekday) => (
              <div key={weekday} className="border-b border-r border-marine/15 bg-silver-lt px-1.5 py-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
                {weekday}
              </div>
            ))}
            <div className="border-b border-r border-marine/15 bg-silver-lt px-1.5 py-1 text-right text-[9px] font-bold uppercase tracking-wide text-ink-mid">Week</div>
            {weeks.map((week) => (
              <div key={week.days[0]?.date} className="contents">
                {week.days.map((day) => (
                  <DayCell key={day.date} day={day} seat={seat} />
                ))}
                <div className="flex items-center justify-end border-b border-r border-marine/15 px-1.5 text-[11px] font-black tabular-nums text-ink">{week.hours} h</div>
              </div>
            ))}
          </div>
        </div>
        <Legend />
        {preferences.isError && <p className="text-[11px] text-ink-mid">PTO and days off could not be read, so only the shifts are shown.</p>}
      </div>
    )
  }

  return (
    <Modal
      eyebrow="Worker's month"
      title={name}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-ink-mid">
            <Lock className="h-3 w-3" aria-hidden="true" />
            Read only. The month as released, with every swap and pickup since.
          </span>
          <Button icon={X} size="sm" variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {body}
    </Modal>
  )
}
